<template>
    <div
        class="w-full flex items-center rounded-xl md:rounded-l-xl md:rounded-r-none bg-zinc-100 border-2 border-zinc-50"
    >
        <div class="px-3 border-r border-zinc-200 rounded-l-xl">
            <svg
                class="text-neutral-900"
                width="21"
                height="25"
                viewBox="0 0 21 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M19.3398 10.2078C19.3398 17.2182 10.3398 23.2272 10.3398 23.2272C10.3398 23.2272 1.33984 17.2182 1.33984 10.2078C1.33984 7.81727 2.28806 5.52466 3.97588 3.83431C5.66371 2.14396 7.9529 1.19434 10.3398 1.19434C12.7268 1.19434 15.016 2.14396 16.7038 3.83431C18.3916 5.52466 19.3398 7.81727 19.3398 10.2078Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
                <path
                    d="M10.3398 13.2121C11.9967 13.2121 13.3398 11.8669 13.3398 10.2076C13.3398 8.54828 11.9967 7.20312 10.3398 7.20312C8.68299 7.20312 7.33984 8.54828 7.33984 10.2076C7.33984 11.8669 8.68299 13.2121 10.3398 13.2121Z"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                />
            </svg>
        </div>
        <label for="inputLocation" class="sr-only"
            >Where do you need a lawyer?</label
        >
        <div class="relative w-full">
            <input
                type="text"
                id="inputLocation"
                ref="inputLocation"
                data-hj-allow
                class="w-full border-0 px-3 py-4 text-xl font-division-heading md:py-6 bg-transparent leading-tight rounded-r-xl md:rounded-r-none focus:outline-none outline-none ring-0 focus:ring-0"
                placeholder="Where do you need a lawyer?"
                autocomplete="off"
                v-model="query"
                autofocus="true"
                role="searchbox"
                @touchstart="scrollToSearchInput"
                @keydown.down="down"
                @keydown.up="up"
                @keydown.enter="onPressEnter"
                @keydown.esc="reset"
                @keydown.delete="clearSelection"
                @input="update"
                @mouseenter="isHovering = true"
                @mouseleave="isHovering = false"
            />

            <ul
                class="absolute w-full top-full text-lg bg-zinc-50 shadow-xl rounded-b-xl overflow-hidden z-10"
                :class="[hasItems ? 'border border-t-0 border-zinc-200' : '']"
            >
                <li
                    v-show="hasItems"
                    v-for="(item, index) in items"
                    :key="index"
                    class="cursor-pointer"
                    :class="activeClass(index)"
                    @mousedown="hit"
                    @mousemove="setActive(index)"
                    :dusk="`search-result-${index}`"
                >
                    <span
                        class="block px-4 py-4 text-neutral-900 border-t border-zinc-200"
                    >
                        <span v-if="isZipCode"
                            ><b>{{ item.ZipCode }}</b>
                            {{ item.City + ", " + item.State }}</span
                        >
                        <span v-else>{{ item.City + ", " + item.State }}</span>
                    </span>
                </li>

                <li
                    v-if="
                        items.length === 0 &&
                        minCharsTyped &&
                        selected === null &&
                        searchWasPerformed
                    "
                >
                    <span
                        class="block px-4 py-4 text-neutral-900 border-t border-zinc-200"
                        >{{ notFoundMessage }}</span
                    >
                </li>
            </ul>
        </div>
    </div>
</template>

<script>
import VueTypeahead from "vue-typeahead";
import _debounce from "lodash/debounce";

export default {
    extends: VueTypeahead,

    props: {
        locations: Object,

        city: {
            type: String,
            default: "",
        },
    },

    data() {
        return {
            selected: null,
            alreadyLoaded: false,
            src: "/api/locations",
            minChars: 3,
            searchWasPerformed: false,
            hasPressedEnter: false,
            isHovering: false,

            previousDataLength: null,
            previousQuery: null,
        };
    },

    methods: {
        update: _debounce(function () {
            if (this.checkIfBeingSpammed()) {
                return;
            }

            this.$emit("input", this.query);
            this.cancel();

            if (!this.query) {
                return this.reset();
            }

            if (this.query.length < this.minChars) {
                return;
            }

            this.loading = true;
            this.$emit("error", "");

            this.fetch()
                .then((response) => {
                    if (!response) {
                        return;
                    }

                    const responseData = response.data;

                    if (responseData && this.query) {
                        let data = this.prepareResponseData
                            ? this.prepareResponseData(responseData)
                            : responseData;

                        this.items = data;

                        this.recordPreviousForSpamming(data);

                        if (this.hasPressedEnter && this.items.length === 1) {
                            this.onHit(this.items[0]);

                            this.hasPressedEnter = false;

                            return;
                        }

                        this.hasPressedEnter = false;

                        if (this.selectFirst) {
                            this.down();
                        }
                    }
                })
                .finally(() => {
                    this.current = -1;
                    this.loading = false;
                    this.searchWasPerformed = true;
                });
        }, 150),

        scrollToSearchInput() {

            if (window.innerWidth > 450) {
                // Don't scroll on desktop and tablet devices
                return;
            }

            const id = "inputLocation";
            const yOffset = -25;
            const element = document.getElementById(id);
            const elementTop = element.getBoundingClientRect().top;

            if (elementTop > 0 && elementTop < window.innerHeight / 3 * 2) {
                return;
            }

            const y = elementTop + window.scrollY + yOffset;

            window.scrollTo({ top: y, behavior: "smooth" });
        },

        onHit(item) {
            if (typeof item === "undefined") {
                return;
            }

            let itemCount = this.items.length || 0;
            this.selected = item;
            this.query = item.City + ", " + item.State;
            this.items = [];

            this.scrollToSearchInput();

            this.$emit("location:selected", item);

            if (this.hasPressedEnter || itemCount === 1) {
                Vue.nextTick(() => {
                    if (this.selected) {
                        this.$emit("submit");
                    }
                });
            }
        },

        onPressEnter() {
            if (this.items.length > 1 && !this.selected) {
                this.$emit(
                    "error",
                    "Please select one of the locations from the list."
                );
            }

            if (this.items.length === 0 && !this.selected) {
                if (this.query == "") {
                    this.$emit("error", "Please enter a location.");
                } else if (this.typedQueryIsString) {
                    this.$emit("error", this.typedQueryIsStringErrorMessage);

                    return;
                } else if (!this.isZipCode) {
                    this.$emit("error", "Please enter a valid Zip Code.");
                }
            }

            if (this.items.length === 0 && this.selected) {
                this.$emit("submit");
            }

            if (this.current !== -1) {
                this.onHit(this.items[this.current]);
            }

            if (this.items.length === 1) {
                this.onHit(this.items[0]);
            }

            this.hasPressedEnter = true;
        },

        prepareResponseData(data) {
            if (this.alreadyLoaded === false) {
                Vue.nextTick(() => {
                    this.alreadyLoaded = true;
                });
            }

            return data.data;
        },

        clearSelection() {
            this.selected = null;
            this.$emit("location:selected", {});
        },

        checkIfBeingSpammed() {
            return (
                this.query.startsWith(this.previousQuery) &&
                this.previousDataLength === 0
            );
        },

        recordPreviousForSpamming({ length }) {
            this.previousDataLength = length;
            this.previousQuery = this.query;
        },
    },

    created() {
        window.addEventListener(
            "resize",
            this.updateInputPlaceholderOnWindowResize
        );
    },

    destroyed() {
        window.removeEventListener(
            "resize",
            this.updateInputPlaceholderOnWindowResize
        );
    },

    computed: {
        isZipCode() {
            var r = RegExp("^[0-9]{5}");
            return r.test(this.query || "");
        },

        minCharsTyped() {
            if (!this.alreadyLoaded) {
                return false;
            }

            if (this.typedQueryIsString) {
                return (this.query || "").length >= 3;
            }

            return (this.query || "").length >= 5;
        },

        notFoundMessage() {
            return this.typedQueryIsString
                ? "City not found"
                : "Zip Code not found";
        },

        typedQueryIsString() {
            return isNaN(this.query);
        },

        typedQueryIsStringErrorMessage() {
            return window.innerWidth > 767 // md or larger
                ? "Please enter a valid City or County where you need a lawyer."
                : "Please type a valid location.";
        },

        splitTestVariation() {
            return "URLSearchParams" in window
                ? parseInt(
                      new URLSearchParams(window.location.search).get(
                          "opv26"
                      ) || 0
                  )
                : 0;
        },
    },

    watch: {
        query(query) {
            if ((this.query || "").length <= 2) {
                this.searchWasPerformed = false;
            }

            this.minChars = this.typedQueryIsString ? 3 : 5;
        },
    },

    mounted() {
        this.$refs.inputLocation.focus();
        this.query = this.city;
    },
};
</script>

<style scoped>
li.active {
    @apply bg-zinc-100;
}
</style>
